import React, { useEffect, useMemo, useRef } from 'react';

import { useAccountStoreSync } from '@/features/workspace/hooks/use-account-store-sync';
import { useFetchUserAccounts } from '@/features/workspace/hooks/use-fetch-user-accounts';
import * as Logger from '@/lib/monitoring/logger';
import { useAccountState } from '@/contexts/account/account-context';
import { AccountStoresProvider } from '@/features/workspace/providers/account-stores-provider';
import { usePixelDestinationsLoading } from '@/features/workspace/providers/account-stores-context';
import { UserAccountsLoadingContext } from '@/contexts/user-accounts-loading/user-accounts-loading-context';
import { UserAccountsLoadingState } from '@/contexts/user-accounts-loading/types/user-accounts-loading-types';

function AccountInitializerInner({ children }: { children: React.ReactNode }) {
  const mountTime = useRef<number | null>(null);
  if (mountTime.current === null) {
    mountTime.current = performance.now();
  }
  const hasLoggedMount = useRef(false);
  const hasLoggedReady = useRef(false);

  const { isLoading, refetch } = useFetchUserAccounts();
  const { accountId, isValidated } = useAccountState();
  const { isSuccess: destinationsReady } = usePixelDestinationsLoading();

  useAccountStoreSync();

  const loadingState = useMemo<UserAccountsLoadingState>(
    () => ({
      isLoading,
      refetch: () => {
        void refetch();
      },
    }),
    [isLoading, refetch]
  );

  // Log mount
  useEffect(() => {
    if (!hasLoggedMount.current) {
      hasLoggedMount.current = true;
      Logger.debug('AccountInitializer mounted', {
        accountId,
        isValidated,
      });
    }
  }, [accountId, isValidated]);

  // Log when initialization is complete (accounts loaded + validated + destinations ready)
  // Note: Destination catalog is fetched once by AuthProvider, not account-dependent
  useEffect(() => {
    if (!hasLoggedReady.current && !isLoading && isValidated && destinationsReady) {
      hasLoggedReady.current = true;
      const durationMs = Math.round(performance.now() - (mountTime.current ?? 0));
      Logger.info('AccountInitializer ready', {
        accountId,
        durationMs,
        isValidated,
        destinationsReady,
      });
    }
  }, [isLoading, isValidated, destinationsReady, accountId]);

  return (
    <UserAccountsLoadingContext.Provider value={loadingState}>
      {children}
    </UserAccountsLoadingContext.Provider>
  );
}

export function AccountInitializer({ children }: { children: React.ReactNode }) {
  return (
    <AccountStoresProvider>
      <AccountInitializerInner>{children}</AccountInitializerInner>
    </AccountStoresProvider>
  );
}
